import type { GridColDef, GridFilterModel, GridPaginationModel, GridRowsProp, GridSortModel } from '@mui/x-data-grid-pro';
import { TableRows } from '@mui/icons-material';
import { alpha, Box, Paper, Typography } from '@mui/material';
import {
  DataGridPro,

  GridToolbar,

} from '@mui/x-data-grid-pro';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

export interface ModernDataTableProps {
  title?: string;
  subtitle?: string;
  rows: GridRowsProp;
  columns: GridColDef[];
  loading?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  onRowClick?: (params: any) => void;
  density?: 'compact' | 'standard' | 'comfortable';
  hideFooter?: boolean;
  autoHeight?: boolean;
  sx?: any;
  headerAction?: React.ReactNode;
  getDetailPanelContent?: (params: any) => React.ReactNode;
  getDetailPanelHeight?: (params: any) => number | 'auto';
  apiRef?: any;
  detailPanelExpandedRowIds?: Set<any>;
  onDetailPanelExpandedRowIdsChange?: (newIds: Set<any>) => void;
  noRowsOverlay?: React.ComponentType;
}

export const ModernDataTable: React.FC<ModernDataTableProps> = ({
  title,
  subtitle,
  rows,
  columns,
  loading = false,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
  checkboxSelection = false,
  disableRowSelectionOnClick = true,
  onRowClick,
  density = 'comfortable',
  hideFooter = false,
  autoHeight = false,
  sx = {},
  headerAction,
  getDetailPanelContent,
  getDetailPanelHeight,
  apiRef,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  noRowsOverlay,
}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  // Custom No Rows Overlay
  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 400,
        py: 8,
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(192, 90, 58, 0.08) 0%, rgba(212, 130, 94, 0.08) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            border: '3px solid',
            borderColor: alpha('#c05a3a', 0.12),
          }}
        >
          <TableRows sx={{ fontSize: 56, color: alpha('#c05a3a', 0.4) }} />
        </Box>
      </motion.div>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 1,
          fontSize: '1.25rem',
        }}
      >
        No data available
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          maxWidth: 360,
          lineHeight: 1.6,
        }}
      >
        There are no records to display at the moment. Try adjusting your filters or check back later.
      </Typography>
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      <Paper
        elevation={0}
        sx={{
          'borderRadius': 3,
          'overflow': 'hidden',
          'border': '1px solid',
          'borderColor': alpha('#c05a3a', 0.08),
          'background': 'linear-gradient(180deg, #ffffff 0%, #faf8f4 100%)',
          'boxShadow': '0 1px 3px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(192, 90, 58, 0.04)',
          'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04), 0 16px 48px rgba(192, 90, 58, 0.08)',
            borderColor: alpha('#c05a3a', 0.12),
          },
          ...sx,
        }}
      >
        {/* Modern Header */}
        {(title || subtitle || headerAction) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              p: 3,
              pb: 2.5,
              background: 'linear-gradient(135deg, rgba(192, 90, 58, 0.03) 0%, rgba(212, 130, 94, 0.02) 100%)',
              borderBottom: '1px solid',
              borderColor: alpha('#c05a3a', 0.08),
            }}
          >
            <Box sx={{ flex: 1 }}>
              {title && (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.375rem',
                    background: 'linear-gradient(135deg, #c05a3a 0%, #d4825e 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: subtitle ? 0.5 : 0,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {headerAction && (
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                {headerAction}
              </Box>
            )}
          </Box>
        )}

        {/* Modern Data Grid */}
        <Box sx={{ width: '100%' }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={pageSizeOptions}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            onRowClick={onRowClick}
            density={density}
            hideFooter={hideFooter}
            autoHeight={autoHeight}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            apiRef={apiRef}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
            slots={{
              toolbar: GridToolbar,
              noRowsOverlay: noRowsOverlay ?? NoRowsOverlay,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
                printOptions: { disableToolbarButton: true },
                csvOptions: {
                  fileName: `${title?.replace(/\s/g, '_') || 'data'}_export`,
                },
              },
            }}
            sx={{
              'border': 'none',
              'backgroundColor': 'transparent',

              // Toolbar Styling - Modern and Clean
              '& .MuiDataGrid-toolbarContainer': {
                'p': 2,
                'px': 3,
                'gap': 2,
                'borderBottom': '1px solid',
                'borderColor': alpha('#c05a3a', 0.06),
                'backgroundColor': alpha('#fff', 0.5),
                'backdropFilter': 'blur(10px)',

                '& .MuiButton-root': {
                  'fontSize': '0.8125rem',
                  'fontWeight': 600,
                  'textTransform': 'none',
                  'borderRadius': 1.5,
                  'px': 2,
                  'py': 0.75,
                  'color': 'text.secondary',
                  'transition': 'all 0.2s ease',

                  '&:hover': {
                    backgroundColor: alpha('#c05a3a', 0.08),
                    color: 'primary.main',
                  },
                },

                '& .MuiTextField-root': {
                  '& .MuiOutlinedInput-root': {
                    'borderRadius': 2,
                    'fontSize': '0.875rem',
                    'backgroundColor': 'background.paper',
                    'transition': 'all 0.2s ease',

                    '&:hover': {
                      backgroundColor: alpha('#c05a3a', 0.02),
                    },

                    '&.Mui-focused': {
                      'backgroundColor': 'background.paper',
                      'boxShadow': `0 0 0 3px ${alpha('#c05a3a', 0.08)}`,

                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#c05a3a', 0.12),
                    },
                  },
                },
              },

              // Column Headers - Elegant and Modern
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: alpha('#c05a3a', 0.04),
                borderBottom: `2px solid ${alpha('#c05a3a', 0.12)}`,
                borderRadius: 0,
                minHeight: '56px !important',
                maxHeight: '56px !important',
                lineHeight: '56px !important',
              },

              '& .MuiDataGrid-columnHeader': {
                'padding': '0 16px',

                '&:focus, &:focus-within': {
                  outline: 'none',
                },

                '&:hover': {
                  backgroundColor: alpha('#c05a3a', 0.06),
                },
              },

              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '0.8125rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'text.primary',
              },

              '& .MuiDataGrid-iconButtonContainer': {
                '& .MuiIconButton-root': {
                  color: 'primary.main',
                },
              },

              // Column Separators - Subtle
              '& .MuiDataGrid-columnSeparator': {
                color: alpha('#c05a3a', 0.08),
              },

              // Rows - Clean and Spacious
              '& .MuiDataGrid-row': {
                'borderRadius': 0,
                'transition': 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

                '&:hover': {
                  backgroundColor: alpha('#c05a3a', 0.04),
                  cursor: onRowClick ? 'pointer' : 'default',
                  transform: 'scale(1.001)',
                  boxShadow: `inset 3px 0 0 ${alpha('#c05a3a', 0.4)}`,
                },

                '&.Mui-selected': {
                  'backgroundColor': alpha('#c05a3a', 0.08),
                  'boxShadow': `inset 3px 0 0 #c05a3a`,

                  '&:hover': {
                    backgroundColor: alpha('#c05a3a', 0.12),
                  },
                },
              },

              // Cells - Modern Typography
              '& .MuiDataGrid-cell': {
                'borderBottom': `1px solid ${alpha('#000', 0.04)}`,
                'fontSize': '0.9375rem',
                'fontWeight': 400,
                'color': 'text.primary',
                'padding': '16px',
                'lineHeight': 1.5,

                '&:focus, &:focus-within': {
                  outline: 'none',
                },
              },

              // Virtual Scroller
              '& .MuiDataGrid-virtualScroller': {
                'minHeight': autoHeight ? undefined : '500px',
                'backgroundColor': 'background.paper',

                // Custom scrollbar
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: alpha('#000', 0.02),
                },
                '&::-webkit-scrollbar-thumb': {
                  'backgroundColor': alpha('#c05a3a', 0.2),
                  'borderRadius': '4px',

                  '&:hover': {
                    backgroundColor: alpha('#c05a3a', 0.3),
                  },
                },
              },

              // Footer - Modern pagination styling
              '& .MuiDataGrid-footerContainer': {
                'borderTop': `1px solid ${alpha('#c05a3a', 0.1)}`,
                'background': `linear-gradient(180deg, ${alpha('#faf8f4', 0.8)} 0%, ${alpha('#F5F2ED', 0.95)} 100%)`,
                'backdropFilter': 'blur(10px)',
                'minHeight': '64px',
                'padding': '12px 24px',
                'display': 'flex',
                'alignItems': 'center',
                'gap': 2,

                '& .MuiTablePagination-root': {
                  color: 'text.primary',
                  width: '100%',
                },

                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 0,
                  paddingRight: 0,
                  minHeight: '40px',
                },

                '& .MuiTablePagination-selectLabel': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  letterSpacing: '0.02em',
                  marginBottom: 0,
                },

                '& .MuiTablePagination-displayedRows': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  letterSpacing: '0.02em',
                  marginBottom: 0,
                },

                '& .MuiTablePagination-select': {
                  'borderRadius': 2,
                  'backgroundColor': 'background.paper',
                  'border': `1.5px solid ${alpha('#c05a3a', 0.15)}`,
                  'fontWeight': 600,
                  'fontSize': '0.875rem',
                  'padding': '6px 32px 6px 12px',
                  'transition': 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  'boxShadow': `0 1px 2px ${alpha('#000', 0.04)}`,

                  '&:hover': {
                    backgroundColor: alpha('#c05a3a', 0.04),
                    borderColor: alpha('#c05a3a', 0.25),
                    boxShadow: `0 2px 4px ${alpha('#c05a3a', 0.08)}`,
                  },

                  '&:focus': {
                    backgroundColor: 'background.paper',
                    borderColor: 'primary.main',
                    boxShadow: `0 0 0 3px ${alpha('#c05a3a', 0.12)}`,
                  },
                },

                '& .MuiTablePagination-actions': {
                  marginLeft: 2,
                  gap: 0.5,
                },

                '& .MuiIconButton-root': {
                  'color': 'primary.main',
                  'transition': 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  'borderRadius': 2,
                  'padding': '8px',
                  'border': `1px solid ${alpha('#c05a3a', 0.1)}`,
                  'backgroundColor': 'background.paper',

                  '&:hover': {
                    backgroundColor: alpha('#c05a3a', 0.08),
                    borderColor: alpha('#c05a3a', 0.2),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 4px ${alpha('#c05a3a', 0.12)}`,
                  },

                  '&.Mui-disabled': {
                    color: alpha('#000', 0.26),
                    backgroundColor: alpha('#000', 0.02),
                    borderColor: alpha('#000', 0.06),
                  },
                },
              },

              // Loading Overlay
              '& .MuiDataGrid-overlay': {
                backgroundColor: alpha('#fff', 0.8),
                backdropFilter: 'blur(4px)',
              },

              // No Rows Overlay
              '& .MuiDataGrid-overlayWrapper': {
                minHeight: '300px',
              },

              // Hide detail panel toggle button
              '& .MuiDataGrid-detailPanelToggleCell': {
                display: 'none',
              },
              '& .MuiDataGrid-cell--detailPanelToggle': {
                display: 'none',
              },
            }}
          />
        </Box>
      </Paper>
    </motion.div>
  );
};
